// src/appbuilder/chat/chat-chatstyle.js
import React, { useEffect, useMemo, useRef, useCallback } from "react";

/* ─────────────────────────────────────────────────────────
   ② iMessage-like composer: auto-wrap + auto-grow
   - Enter: send
   - Shift+Enter: newline
   - Works on mobile/desktop
   ───────────────────────────────────────────────────────── */

export function useAutoGrowTextArea(value, { maxHeight = 180 } = {}) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Reset then grow to content
    el.style.height = "0px";
    const next = Math.min(el.scrollHeight, maxHeight);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? "auto" : "hidden";
  }, [value, maxHeight]);

  return ref;
}

export function ChatComposerInput({
  value,
  onChange,
  onSend,
  onTyping,
  placeholder = "Message…",
  disabled = false,
  maxHeight = 180,
  className = "lxchat-cmp-input2",
}) {
  const taRef = useAutoGrowTextArea(value, { maxHeight });

  const sendNow = useCallback(() => {
    if (disabled) return;
    onSend?.();
  }, [disabled, onSend]);

  return (
    <textarea
      ref={taRef}
      className={className}
      placeholder={placeholder}
      value={value}
      disabled={disabled}
      rows={1}
      onChange={(e) => {
        onChange?.(e.target.value);
        onTyping?.(true);
      }}
      onBlur={() => onTyping?.(false)}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          sendNow();
          return;
        }
        if (e.key === "Escape") {
          e.preventDefault();
          onChange?.("");
          onTyping?.(false);
        }
        // Shift+Enter: allow default newline
      }}
      aria-label="Message input"
    />
  );
}

/* ─────────────────────────────────────────────────────────
   ③ iMessage-like timestamp dividers (not per-message)
   Rules:
   - show divider on first
   - show divider on day change
   - show divider if gap > thresholdMinutes
   ───────────────────────────────────────────────────────── */

function toDateSafe(ts) {
  if (!ts) return null;
  if (typeof ts?.toDate === "function") return ts.toDate();
  if (typeof ts === "number" || typeof ts === "string") {
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function sameDay(a, b) {
  return (
    a &&
    b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function formatDayLabel(d) {
  try {
    // e.g. Feb 22, 2026
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

function formatTimeLabel(d) {
  try {
    // e.g. 3:14 PM
    return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

export function buildMessageStream(messages = [], { thresholdMinutes = 15 } = {}) {
  const out = [];
  const thrMs = Math.max(1, Number(thresholdMinutes || 15)) * 60 * 1000;

  for (let i = 0; i < (messages || []).length; i++) {
    const m = messages[i];
    const prev = i > 0 ? messages[i - 1] : null;

    const d = toDateSafe(m?.createdAt);
    const pd = toDateSafe(prev?.createdAt);

    const isFirst = i === 0;
    const dayChanged = d && pd && !sameDay(d, pd);
    const gapBig =
      d && pd ? Math.abs(d.getTime() - pd.getTime()) >= thrMs : false;

    const showDivider = !!d && (isFirst || dayChanged || gapBig);

    if (showDivider) {
      // If day changed OR first message of day: show date; else show time
 const label =
  dayChanged || isFirst
    ? `${formatDayLabel(d)} · ${formatTimeLabel(d)}`
    : formatTimeLabel(d);
      out.push({ type: "divider", id: `div_${m?.id || i}`, label });
    }

    out.push({ type: "message", id: m?.id || `m_${i}`, msg: m });
  }

  return out;
}

export function TimeDivider({ label }) {
  if (!label) return null;
  return (
    <div className="lxchat-timediv">
      <span className="lxchat-timediv-line" aria-hidden />
      <span className="lxchat-timediv-text">{label}</span>
      <span className="lxchat-timediv-line" aria-hidden />
    </div>
  );
}

/* Helper hook: memo stream */
export function useMessageStream(messages, opts) {
  return useMemo(() => buildMessageStream(messages || [], opts || {}), [messages, opts]);
}